import { createFileRoute, Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FiArrowLeft } from 'react-icons/fi';
import { Footer, SEO } from '@/components';
import { EmailGateModal, PlanPreview, WizardShell } from '@/components/parenting-plan';
import { Input } from '@/components/Input';
import { API_BASE_URL } from '@/lib/api';
import { track } from '@/lib/analytics';
import type { ParentingPlanAnswers } from '@/lib/parenting-plan-schemas';
import { isScheduleSlug } from '@/lib/parenting-plan-schemas';

const STEPS = [
  { id: 'children', label: 'Children' },
  { id: 'custody', label: 'Custody' },
  { id: 'holidays', label: 'Holidays' },
  { id: 'communication', label: 'Communication' },
  { id: 'expenses', label: 'Expenses' },
  { id: 'decisions', label: 'Decisions' },
  { id: 'review', label: 'Review' },
] as const;

export const Route = createFileRoute('/parenting-plan/builder')({
  validateSearch: (search: Record<string, unknown>) => ({
    claimToken: typeof search.claimToken === 'string' ? search.claimToken : undefined,
    schedule: typeof search.schedule === 'string' ? search.schedule : undefined,
  }),
  component: ParentingPlanBuilderPage,
});

function ParentingPlanBuilderPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: Route.fullPath });
  const [step, setStep] = useState(0);
  const [claimToken, setClaimToken] = useState<string | null>(search.claimToken ?? null);
  const [answers, setAnswers] = useState<ParentingPlanAnswers>({});
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [generateLoading, setGenerateLoading] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadGeneration = useRef(0);

  const persistSearch = useCallback(
    (token: string) => {
      void navigate({
        to: '/parenting-plan/builder',
        search: { claimToken: token, schedule: undefined },
        replace: true,
      });
    },
    [navigate],
  );

  // Initial load: resume draft or create new
  useEffect(() => {
    const gen = ++loadGeneration.current;
    let cancelled = false;
    (async () => {
      try {
        if (search.claimToken) {
          const res = await fetch(`${API_BASE_URL}/v1/parenting-plan/draft/${search.claimToken}`);
          if (!res.ok) throw new Error('Draft not found');
          const data = (await res.json()) as {
            claim_token: string;
            schedule_type: string | null;
            answers: ParentingPlanAnswers;
          };
          if (cancelled || gen !== loadGeneration.current) return;
          setClaimToken(data.claim_token);
          setAnswers(data.answers || {});
          track('Parenting Plan Draft Resumed');
          return;
        }

        const initialSchedule =
          search.schedule && isScheduleSlug(search.schedule) ? search.schedule : undefined;
        const res = await fetch(`${API_BASE_URL}/v1/parenting-plan/draft`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            schedule_type: initialSchedule,
            answers: initialSchedule
              ? { custody: { scheduleSlug: initialSchedule } }
              : {},
          }),
        });
        if (!res.ok) throw new Error('Could not start draft');
        const data = (await res.json()) as { claim_token: string; answers: ParentingPlanAnswers };
        if (cancelled || gen !== loadGeneration.current) return;
        setClaimToken(data.claim_token);
        setAnswers(data.answers || {});
        persistSearch(data.claim_token);
        track('Parenting Plan Draft Created', { schedule: initialSchedule });
      } catch {
        if (!cancelled && gen === loadGeneration.current) {
          setLoadError('We could not load your draft. Please refresh or start from the hub.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [search.claimToken, search.schedule, persistSearch]);

  useEffect(() => {
    track('Parenting Plan Builder View');
  }, []);

  useEffect(() => {
    track('Parenting Plan Step Started', { step: STEPS[step]?.id });
  }, [step]);

  const flushSave = useCallback(async () => {
    if (!claimToken) return;
    setSaving(true);
    try {
      await fetch(`${API_BASE_URL}/v1/parenting-plan/draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          claim_token: claimToken,
          schedule_type: answers.custody?.scheduleSlug ?? undefined,
          answers,
        }),
      });
    } finally {
      setSaving(false);
    }
  }, [claimToken, answers]);

  const scheduleSave = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      void flushSave();
    }, 600);
  }, [flushSave]);

  useEffect(() => {
    if (!claimToken) return;
    scheduleSave();
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [answers, claimToken, scheduleSave]);

  const handleDownload = async (email: string) => {
    if (!claimToken) return;
    setGenerateLoading(true);
    try {
      track('Parenting Plan Email Submitted');
      const w = window as Window & {
        gtagSendEvent?: (eventName: string) => void;
        fbq?: (...args: unknown[]) => void;
      };
      if (typeof w.gtagSendEvent === 'function') {
        w.gtagSendEvent('generate_lead');
      }
      if (typeof w.fbq === 'function') {
        w.fbq('track', 'Lead');
      }

      await flushSave();

      const res = await fetch(`${API_BASE_URL}/v1/parenting-plan/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ claim_token: claimToken, email }),
      });
      if (!res.ok) {
        const t = await res.text().catch(() => '');
        throw new Error(t || 'Generate failed');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'parenting-plan.pdf';
      a.click();
      URL.revokeObjectURL(url);
      track('Parenting Plan PDF Downloaded');
      setEmailOpen(false);
    } finally {
      setGenerateLoading(false);
    }
  };

  const canNext = (() => {
    if (step === 0) {
      const items = answers.children?.items?.filter((c) => c.name?.trim()) ?? [];
      return items.length > 0;
    }
    if (step === 1) {
      return Boolean(answers.custody?.scheduleSlug && answers.custody?.startDate);
    }
    return true;
  })();

  const isLastStep = step === STEPS.length - 1;
  const nextLabel = isLastStep ? 'Download PDF' : 'Continue';

  const goNext = async () => {
    if (isLastStep) {
      await flushSave();
      track('Parenting Plan Step Completed', { step: STEPS[step]?.id });
      setEmailOpen(true);
      return;
    }
    track('Parenting Plan Step Completed', { step: STEPS[step]?.id });
    setStep((s) => s + 1);
  };

  const goBack = () => {
    if (step > 0) setStep((s) => s - 1);
  };

  const nunito = {
    fontFamily:
      "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  };

  if (loadError) {
    return (
      <div className='min-h-screen bg-white px-6 py-24 text-center'>
        <p className='text-gray-700'>{loadError}</p>
        <Link to='/parenting-plan' className='mt-6 inline-block text-primary font-semibold'>
          Back to hub
        </Link>
      </div>
    );
  }

  if (!claimToken) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-white'>
        <p className='text-gray-600'>Starting your plan…</p>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gray-50'>
      <SEO
        title='Parenting plan builder | Harbor'
        description='Draft your parenting plan in Harbor.'
        path='/parenting-plan/builder'
        noindex
      />
      <header className='border-b border-gray-200 bg-white'>
        <div className='mx-auto flex max-w-6xl items-center justify-between px-4 py-4'>
          <Link
            to='/parenting-plan'
            className='inline-flex items-center gap-2 text-sm font-medium text-primary'
            style={nunito}
          >
            <FiArrowLeft /> Parenting plan hub
          </Link>
          <span className='text-xs text-gray-500'>{saving ? 'Saving…' : 'Saved'}</span>
        </div>
      </header>

      <WizardShell
        steps={[...STEPS]}
        activeIndex={step}
        onBack={goBack}
        onNext={goNext}
        canGoBack={step > 0}
        canGoNext={canNext}
        nextLabel={nextLabel}
        sidePanel={
          <div className='rounded-xl border border-gray-200 bg-white p-4 shadow-sm lg:sticky lg:top-24'>
            <PlanPreview answers={answers} />
          </div>
        }
      >
        {step === 0 && (
          <ChildrenStep answers={answers} setAnswers={setAnswers} />
        )}
        {step === 1 && <CustodyStep answers={answers} setAnswers={setAnswers} />}
        {step === 2 && (
          <div>
            <h2 className='mb-2 text-2xl font-bold text-gray-900' style={nunito}>
              Holidays & school breaks
            </h2>
            <p className='mb-4 text-gray-600' style={nunito}>
              Summarize how you want major holidays and breaks to work.
            </p>
            <textarea
              className='w-full min-h-[140px] rounded-xl border border-gray-200 p-4 text-gray-900'
              placeholder='e.g. Thanksgiving alternates each year; winter break split 50/50…'
              value={answers.holidays?.notes ?? ''}
              onChange={(e) =>
                setAnswers((a) => ({ ...a, holidays: { ...a.holidays, notes: e.target.value } }))
              }
            />
          </div>
        )}
        {step === 3 && (
          <div className='space-y-4'>
            <h2 className='text-2xl font-bold text-gray-900' style={nunito}>
              Communication
            </h2>
            <Input
              label='Preferred written channel'
              placeholder='e.g. Harbor chat, email'
              value={answers.communication?.channel ?? ''}
              onChange={(e) =>
                setAnswers((a) => ({
                  ...a,
                  communication: { ...a.communication, channel: e.target.value },
                }))
              }
            />
            <Input
              label='Expected response time (non-emergency)'
              placeholder='e.g. within 24–48 hours on weekdays'
              value={answers.communication?.responseTime ?? ''}
              onChange={(e) =>
                setAnswers((a) => ({
                  ...a,
                  communication: { ...a.communication, responseTime: e.target.value },
                }))
              }
            />
            <textarea
              className='w-full min-h-[100px] rounded-xl border border-gray-200 p-4'
              placeholder='Other communication rules…'
              value={answers.communication?.notes ?? ''}
              onChange={(e) =>
                setAnswers((a) => ({
                  ...a,
                  communication: { ...a.communication, notes: e.target.value },
                }))
              }
            />
          </div>
        )}
        {step === 4 && (
          <div className='space-y-4'>
            <h2 className='text-2xl font-bold text-gray-900' style={nunito}>
              Shared expenses
            </h2>
            <Input
              label='Default split / reimbursement approach'
              placeholder='e.g. 50/50 after receipt; medical billed to insurance first'
              value={answers.expenses?.split ?? ''}
              onChange={(e) =>
                setAnswers((a) => ({
                  ...a,
                  expenses: { ...a.expenses, split: e.target.value },
                }))
              }
            />
            <textarea
              className='w-full min-h-[120px] rounded-xl border border-gray-200 p-4'
              placeholder='Categories to share, receipt rules, apps used…'
              value={answers.expenses?.notes ?? ''}
              onChange={(e) =>
                setAnswers((a) => ({
                  ...a,
                  expenses: { ...a.expenses, notes: e.target.value },
                }))
              }
            />
          </div>
        )}
        {step === 5 && (
          <div>
            <h2 className='mb-2 text-2xl font-bold text-gray-900' style={nunito}>
              Major decisions & disputes
            </h2>
            <textarea
              className='w-full min-h-[160px] rounded-xl border border-gray-200 p-4'
              placeholder='Education, medical, activities; mediation before court motions…'
              value={answers.decisions?.notes ?? ''}
              onChange={(e) =>
                setAnswers((a) => ({
                  ...a,
                  decisions: { ...a.decisions, notes: e.target.value },
                }))
              }
            />
          </div>
        )}
        {step === 6 && (
          <div className='space-y-6'>
            <h2 className='text-2xl font-bold text-gray-900' style={nunito}>
              Review & download
            </h2>
            <p className='text-gray-600' style={nunito}>
              When you&apos;re ready, download a PDF. We&apos;ll email you a copy with a link to open Harbor
              and import your custody pattern (when you have a child profile set up).
            </p>
            <div className='flex flex-wrap gap-3'>
              <a
                href='https://apps.apple.com/app/harbor-co-parenting/id6741088558'
                target='_blank'
                rel='noreferrer'
                className='inline-flex items-center rounded-2xl border border-gray-300 px-5 py-3 text-sm font-semibold text-gray-800'
              >
                Get Harbor app
              </a>
            </div>
          </div>
        )}
      </WizardShell>

      <EmailGateModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        onSubmit={handleDownload}
        loading={generateLoading}
      />

      <Footer />
    </div>
  );
}

function ChildrenStep({
  answers,
  setAnswers,
}: {
  answers: ParentingPlanAnswers;
  setAnswers: React.Dispatch<React.SetStateAction<ParentingPlanAnswers>>;
}) {
  const items = answers.children?.items?.length ? answers.children.items : [{ name: '', birthYear: '' }];

  const update = (idx: number, field: 'name' | 'birthYear', value: string) => {
    setAnswers((a) => {
      const next = [...(a.children?.items ?? [{ name: '', birthYear: '' }])];
      next[idx] = { ...next[idx], [field]: value };
      return { ...a, children: { items: next } };
    });
  };

  const add = () => {
    setAnswers((a) => ({
      ...a,
      children: { items: [...(a.children?.items ?? []), { name: '', birthYear: '' }] },
    }));
  };

  return (
    <div>
      <h2 className='mb-2 text-2xl font-bold text-gray-900'>Children</h2>
      <p className='mb-6 text-gray-600'>Add at least one child to continue.</p>
      <div className='space-y-4'>
        {items.map((row, idx) => (
          <div key={idx} className='flex flex-wrap gap-3'>
            <div className='min-w-[200px] flex-1'>
              <label className='mb-1 block text-sm text-gray-600'>First name</label>
              <Input value={row.name ?? ''} onChange={(e) => update(idx, 'name', e.target.value)} />
            </div>
            <div className='w-32'>
              <label className='mb-1 block text-sm text-gray-600'>Birth year</label>
              <Input value={row.birthYear ?? ''} onChange={(e) => update(idx, 'birthYear', e.target.value)} />
            </div>
          </div>
        ))}
      </div>
      <button type='button' className='mt-4 text-sm font-semibold text-primary' onClick={add}>
        + Add another child
      </button>
    </div>
  );
}

function CustodyStep({
  answers,
  setAnswers,
}: {
  answers: ParentingPlanAnswers;
  setAnswers: React.Dispatch<React.SetStateAction<ParentingPlanAnswers>>;
}) {
  const slug = answers.custody?.scheduleSlug ?? '';
  return (
    <div className='space-y-4'>
      <h2 className='text-2xl font-bold text-gray-900'>Custody schedule</h2>
      <p className='text-gray-600'>Pick the pattern that best matches what you want to propose.</p>
      <div>
        <label className='mb-1 block text-sm font-medium text-gray-700'>Schedule type</label>
        <select
          className='w-full max-w-md rounded-xl border border-gray-200 bg-white px-4 py-3'
          value={slug}
          onChange={(e) =>
            setAnswers((a) => ({
              ...a,
              custody: { ...a.custody, scheduleSlug: e.target.value },
            }))
          }
        >
          <option value=''>Select…</option>
          <option value='2-2-3'>2-2-3</option>
          <option value='2-2-5-5'>2-2-5-5</option>
          <option value='week-on-week-off'>Week on / week off</option>
          <option value='3-4-4-3'>3-4-4-3</option>
          <option value='50-50'>50/50 (week-based)</option>
          <option value='60-40'>60/40</option>
          <option value='long-distance'>Long distance</option>
          <option value='newborn'>Newborn-focused</option>
          <option value='first-time'>First parenting plan</option>
          <option value='holidays-only'>Holidays & breaks focus</option>
          <option value='summer'>Summer / extended time</option>
          <option value='every-extended-weekend'>Extended weekends</option>
        </select>
      </div>
      <div>
        <label className='mb-1 block text-sm font-medium text-gray-700'>Pattern start date</label>
        <Input
          type='date'
          value={answers.custody?.startDate ?? ''}
          onChange={(e) =>
            setAnswers((a) => ({
              ...a,
              custody: { ...a.custody, startDate: e.target.value },
            }))
          }
        />
      </div>
      <div>
        <label className='mb-1 block text-sm font-medium text-gray-700'>Pick-up / drop-off notes (optional)</label>
        <textarea
          className='w-full min-h-[100px] rounded-xl border border-gray-200 p-4'
          value={answers.custody?.notes ?? ''}
          onChange={(e) =>
            setAnswers((a) => ({
              ...a,
              custody: { ...a.custody, notes: e.target.value },
            }))
          }
        />
      </div>
    </div>
  );
}
